'use client'

import Skeleton from "@/components/elements/Skeleton"
import { formatImageURL, isLoyaltyActive } from "@/utils/util"
import { getInitials } from "@/utils/avatar"
import Link from "next/link"
import { useProfileNav } from "@/lib/commerce/hooks"

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground'
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const sweepLine = (active: boolean) => `pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${active ? 'bg-size-[100%_1px]' : 'bg-size-[0%_1px] group-hover:bg-size-[100%_1px]'}`

function useVisibleNav(vendor: any) {
  const { accountItems, logoutItem } = useProfileNav()
  return { items: accountItems.filter((item) => item.key !== 'loyalty' || isLoyaltyActive(vendor)), logoutItem }
}

export default function ProfileCard({ customer, vendor, isLoading = false, onLinkClick }: { customer: any, vendor: any, isLoading?: boolean, setIsLoading?: (isLoading: boolean) => void, onLinkClick?: () => void }) {
  const { items, logoutItem } = useVisibleNav(vendor)
  const fullName = [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') || 'Хэрэглэгч'

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center gap-4">
          <Skeleton className="size-14 rounded-none" />
          <div className="space-y-2">
            <Skeleton className="w-36 h-4 rounded-none" />
            <Skeleton className="w-44 h-3 rounded-none" />
          </div>
        </div>
        <div className="mt-10 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="w-28 h-4 rounded-none" />)}
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-4">
        <div className="size-14 shrink-0 bg-muted overflow-hidden grid place-items-center text-sm tracking-wide text-muted-foreground">
          {customer?.avatar ? <img src={formatImageURL(customer.avatar)} alt="" className="w-full h-full object-cover" /> : getInitials(fullName)}
        </div>
        <div className="min-w-0">
          <div className="text-base text-foreground truncate">{fullName}</div>
          {customer?.email && <div className="mt-0.5 text-xs text-muted-foreground truncate">{customer.email}</div>}
        </div>
      </div>

      <nav aria-label="Миний бүртгэл" className="mt-10">
        <div className={eyebrow}>Бүртгэл</div>
        <ul className="mt-4 space-y-1">
          {items.map((item) => (
            <li key={item.key}>
              <Link href={item.url} onClick={onLinkClick} aria-current={item.isActive ? 'page' : undefined} className={`group inline-block py-1.5 text-[15px] transition-colors duration-150 ${item.isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'} ${focusRing}`}>
                <span className={sweepLine(item.isActive)}>{item.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {logoutItem && (
        <div className="mt-10 pt-6 border-t border-border">
          <Link href={logoutItem.url} onClick={onLinkClick} className={`text-[13px] text-muted-foreground transition-colors duration-150 hover:text-foreground ${focusRing}`}>
            {logoutItem.name}
          </Link>
        </div>
      )}
    </div>
  )
}

export function ProfileTabs({ vendor }: { vendor: any }) {
  const { items } = useVisibleNav(vendor)
  return (
    <nav aria-label="Миний бүртгэл" className="flex items-center gap-6 border-b border-border overflow-x-auto scrollbar-hide -mx-4 px-4">
      {items.map((item) => (
        <Link key={item.key} href={item.url} aria-current={item.isActive ? 'page' : undefined} className={`shrink-0 -mb-px py-3 border-b text-[13px] whitespace-nowrap transition-colors duration-150 ${item.isActive ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'} ${focusRing}`}>
          {item.name}
        </Link>
      ))}
    </nav>
  )
}
